import React, { useState, useEffect } from 'react';
import ColaboradorLayout from '../../components/ColaboradorLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { assignmentsAPI, requestsAPI, waitlistAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Send, Loader2, Calendar } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const RequestsPage = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [changeForm, setChangeForm] = useState({
    assignment_id: '',
    requested_date: '',
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [assignRes, reqRes] = await Promise.all([
        assignmentsAPI.getAll({}),
        requestsAPI.getAll()
      ]);
      setAssignments(assignRes.data);
      setMyRequests(reqRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestsAPI.createChange(changeForm);
      toast.success('Solicitud de cambio enviada');
      setChangeForm({ assignment_id: '', requested_date: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWaitlistRequest = async () => {
    if (!changeForm.requested_date) {
      toast.error('Selecciona una fecha');
      return;
    }
    setSubmitting(true);
    try {
      await waitlistAPI.add(user?.sede_id || '', changeForm.requested_date);
      toast.success('Añadido a lista de espera');
      setChangeForm({ ...changeForm, requested_date: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir a lista de espera');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return 'badge-success';
      case 'rejected': return 'badge-error';
      default: return 'badge-warning';
    }
  };

  return (
    <ColaboradorLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Solicitar Cambio
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Solicita cambios en tus días asignados
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Request Form */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[#002FA7]" />
                  Nueva Solicitud
                </CardTitle>
                <CardDescription>
                  Solicita cambiar uno de tus días asignados
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="change">
                  <TabsList className="mb-4">
                    <TabsTrigger value="change">Cambio de día</TabsTrigger>
                    <TabsTrigger value="waitlist">Lista de espera</TabsTrigger>
                  </TabsList>

                  <TabsContent value="change">
                    <form onSubmit={handleChangeRequest} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Día asignado a cambiar</Label>
                        <Select 
                          value={changeForm.assignment_id} 
                          onValueChange={(value) => setChangeForm({ ...changeForm, assignment_id: value })}
                        >
                          <SelectTrigger className="rounded-sm" data-testid="change-assignment-select">
                            <SelectValue placeholder="Selecciona tu día asignado" />
                          </SelectTrigger>
                          <SelectContent>
                            {assignments.filter((a) => {
                              const [y, m, d] = a.date.split('-').map(Number);
                              const todayChile = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
                              return a.date >= todayChile;
                            }).map((a) => {
                              const [y, m, d] = a.date.split('-').map(Number);
                              return (
                              <SelectItem key={a.id} value={a.id}>
                                {format(new Date(y, m - 1, d), 'EEEE d MMMM', { locale: es })}
                              </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Nueva fecha solicitada</Label>
                        <Input
                          type="date"
                          value={changeForm.requested_date}
                          onChange={(e) => setChangeForm({ ...changeForm, requested_date: e.target.value })}
                          min={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' })}
                          className="rounded-sm"
                          required
                          data-testid="change-date-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Motivo del cambio</Label>
                        <Textarea
                          value={changeForm.reason}
                          onChange={(e) => setChangeForm({ ...changeForm, reason: e.target.value })}
                          placeholder="Explica el motivo de tu solicitud..."
                          className="rounded-sm"
                          required
                          data-testid="change-reason-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                        disabled={submitting || !changeForm.assignment_id}
                        data-testid="submit-change-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Enviar Solicitud
                          </>
                        )}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="waitlist">
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600">
                        Si necesitas asistir un día que no tienes asignado, puedes añadirte a la 
                        lista de espera. Si se libera un cupo, se te notificará.
                      </p>

                      <div className="space-y-2">
                        <Label>Fecha deseada</Label>
                        <Input
                          type="date"
                          value={changeForm.requested_date}
                          onChange={(e) => setChangeForm({ ...changeForm, requested_date: e.target.value })}
                          min={new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santiago' })}
                          className="rounded-sm"
                          data-testid="waitlist-date-input"
                        />
                      </div>

                      <Button
                        onClick={handleWaitlistRequest}
                        className="w-full bg-[#002FA7] hover:bg-[#001B66] rounded-sm"
                        disabled={submitting || !changeForm.requested_date}
                        data-testid="submit-waitlist-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Añadiendo...
                          </>
                        ) : (
                          <>
                            <Calendar className="w-4 h-4 mr-2" />
                            Añadir a Lista de Espera
                          </>
                        )}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* My Requests */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Mis Solicitudes</CardTitle>
              </CardHeader>
              <CardContent>
                {myRequests.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <RefreshCw className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p>No tienes solicitudes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myRequests.map((req) => (
                      <div key={req.id} className="p-3 bg-slate-50 rounded-sm border border-slate-200">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className={getStatusBadge(req.status)}>
                              {req.status === 'pending' ? 'Pendiente' : req.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                            </span>
                            <span className="badge-info ml-2">
                              {req.request_type === 'change' ? 'Cambio' : 'Intercambio'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {req.created_at?.split('T')[0]}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">{req.reason}</p>
                        {req.details?.requested_date && (
                          <p className="text-xs text-slate-500 mt-1">
                            Fecha solicitada: {req.details.requested_date}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ColaboradorLayout>
  );
};

export default RequestsPage;
